import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN, pricePerKg } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import WishlistButton from "@/components/WishlistButton";
import SafeImage from "@/components/SafeImage";

const CATEGORY_LABELS = {
  cortes: "Cortes Premium",
  charcuteria: "Charcutería",
  quesos: "Quesos",
  complementos: "Complementos",
  condimentos: "Condimentos",
  extras: "Extras",
};
const CATEGORY_TAGLINES = {
  cortes: "100% carne sonorense",
  charcuteria: "Embutidos artesanales",
  quesos: "Para acompañar el asado",
  complementos: "Lo que no puede faltar",
  condimentos: "Sal, especias y más",
  extras: "Hielera, carbón y guías",
};

export default function CatalogPage() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    const url = category ? `/products?category=${category}` : "/products";
    api.get(url).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category]);

  const title = category ? CATEGORY_LABELS[category] || "Catálogo" : "Todo el catálogo";
  const tagline = category ? CATEGORY_TAGLINES[category] || "" : "Explora toda la boutique";

  return (
    <div className="bg-arre-black min-h-screen" data-testid="catalog-page">
      {/* Header */}
      <section className="pt-16 pb-12 border-b border-arre-border">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Catálogo</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-none">{title}</h1>
          <p className="font-serif-display italic text-xl text-white/70 mt-4">{tagline}</p>
        </div>
      </section>

      {/* Category tabs */}
      <div className="border-b border-arre-border bg-arre-black/80 sticky top-20 z-30 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 overflow-x-auto">
          <div className="flex gap-8 py-5 min-w-max">
            {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
              <Link
                key={slug}
                to={`/catalogo/${slug}`}
                className={`font-display text-lg tracking-wider uppercase transition whitespace-nowrap ${
                  category === slug ? "text-arre-red" : "text-white/50 hover:text-white"
                }`}
                data-testid={`cat-tab-${slug}`}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-arre-surface animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-3xl text-white/50 tracking-wider uppercase">Sin productos en esta categoría</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => {
                const hasMulti = (p.variants?.length || 0) > 1;
                const startVariant = p.variants?.[0];
                const startPrice = startVariant?.price ?? p.price;
                const startWeight = startVariant?.weight_g ?? p.weight_g;
                const perKg = pricePerKg({ price: startPrice, weight_g: startWeight });
                return (
                  <Link
                    to={`/producto/${p.slug}`}
                    key={p.id}
                    className="group block bg-arre-surface border border-arre-border hover:border-arre-red transition-colors hover-zoom"
                    data-testid={`product-card-${p.slug}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-arre-black relative">
                      <SafeImage src={p.image} alt={p.name} fallbackLabel={p.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                      <div className="absolute top-3 right-3" onClick={(e) => e.preventDefault()}>
                        <WishlistButton productId={p.id} />
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1">
                        {hasMulti ? "350 g · 1 kg" : p.unit}
                      </div>
                      <h3 className="font-display text-2xl tracking-wider uppercase text-white leading-none">{p.name}</h3>
                      <p className="text-xs text-white/50 mt-1 line-clamp-2">{p.short_desc}</p>
                      <div className="flex items-end justify-between mt-4 gap-2">
                        <div>
                          {hasMulti && <div className="text-[9px] tracking-widest uppercase text-white/40">desde</div>}
                          <span className="font-display text-xl text-white">{fmtMXN(startPrice)}</span>
                          {perKg && (
                            <div className="text-[10px] tracking-widest uppercase text-white/50 mt-0.5" data-testid={`price-per-kg-${p.slug}`}>
                              {perKg} / kg
                            </div>
                          )}
                        </div>
                        {hasMulti ? (
                          <span className="text-[10px] tracking-[0.25em] uppercase text-arre-red font-bold">
                            Elegir →
                          </span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              const variant = p.variants?.[0];
                              addItem({
                                type: "product",
                                id: p.id,
                                variant_id: variant?.id || null,
                                name: variant ? `${p.name} — ${variant.label}` : p.name,
                                price: variant?.price ?? p.price,
                                image: p.image,
                                unit: variant?.label || p.unit,
                              });
                            }}
                            className="text-[10px] tracking-[0.25em] uppercase text-arre-red hover:text-white font-bold"
                            data-testid={`add-to-cart-${p.slug}`}
                          >
                            + Agregar
                          </button>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
